/**
 * Helper functions for Structure Tree rendering and manipulation
 */
import React from "react"
import { StructureNode, NodeType } from "./structureParser"

/**
 * Get question type label from structure node
 * Returns just the question type (e.g., "Single Select", "Grid Multiple Select", etc.)
 */
const getQuestionTypeFromNode = (node: StructureNode): string | null => {
  if (node.type !== NodeType.QUESTION || !node.metadata?.contentLines)
    return null

  const contentText = node.metadata.contentLines.join("\n")
  const hasPER = node.metadata.contentLines.some((line) =>
    line.trim().match(/^PER\b/i)
  )

  // Check for FILL IN types first
  if (contentText.match(/FILL\s+IN\s+NUMBER/i)) {
    return "Fill In Number"
  }
  if (contentText.match(/FILL\s+IN\s+TEXT/i)) {
    return "Fill In Text"
  }

  // Check for SELECT types
  const hasSelectOne = contentText.match(/SELECT\s+ONE\s+FROM/i)
  const hasSelectMultiple = contentText.match(/SELECT\s+MULTIPLE\s+FROM/i)

  if (hasPER) {
    // Grid questions
    if (hasSelectOne) {
      return "Grid Single Select"
    }
    if (hasSelectMultiple) {
      return "Grid Multiple Select"
    }
    return "Grid Question"
  } else {
    // Non-grid questions
    if (hasSelectOne) {
      return "Single Select"
    }
    if (hasSelectMultiple) {
      return "Multiple Select"
    }
  }

  return null
}

/**
 * Get tag component for node type
 */
export const getNodeTag = (node: StructureNode): React.ReactNode => {
  const tagStyle = {
    display: "inline-block",
    padding: "2px 7px",
    borderRadius: "3px",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.02em",
    marginRight: "6px",
    textTransform: "uppercase" as const,
  }

  const type = node.type

  switch (type) {
    case NodeType.SURVEY:
      return (
        <span
          style={{
            ...tagStyle,
            background: "rgba(189, 195, 199, 0.2)",
            color: "#BDC3C7",
          }}
        >
          SURVEY
        </span>
      )
    case NodeType.SECTION:
      return (
        <span
          style={{
            ...tagStyle,
            background: "rgba(22, 163, 74, 0.2)",
            color: "#16A34A",
          }}
        >
          SECTION
        </span>
      )
    case NodeType.QUESTION: {
      const questionType = getQuestionTypeFromNode(node)
      const questionLabel = questionType
        ? `QUESTION - ${questionType}`
        : "QUESTION"
      return (
        <span
          style={{
            ...tagStyle,
            background: "rgba(54, 195, 237, 0.2)",
            color: "#36C3ED",
          }}
        >
          {questionLabel}
        </span>
      )
    }
    case NodeType.LIST:
      return (
        <span
          style={{
            ...tagStyle,
            background: "rgba(230, 126, 34, 0.2)",
            color: "#E67E22",
          }}
        >
          LIST
        </span>
      )
    case NodeType.RANGE_LIST:
      return (
        <span
          style={{
            ...tagStyle,
            background: "rgba(230, 126, 34, 0.2)",
            color: "#E67E22",
          }}
        >
          RANGE LIST
        </span>
      )
    case NodeType.EXCEPTIONS:
      return (
        <span
          style={{
            ...tagStyle,
            background: "rgba(231, 76, 60, 0.2)",
            color: "#E74C3C",
          }}
        >
          EXCEPTIONS
        </span>
      )
    case NodeType.EXCEPTION_ITEM:
      return (
        <span
          style={{
            ...tagStyle,
            background: "rgba(231, 76, 60, 0.2)",
            color: "#E74C3C",
          }}
        >
          EXCEPTION
        </span>
      )
    case NodeType.TEXT:
      return (
        <span
          style={{
            ...tagStyle,
            background: "rgba(39, 174, 96, 0.2)",
            color: "#27AE60",
          }}
        >
          TEXT
        </span>
      )
    case NodeType.NOTE:
      return (
        <span
          style={{
            ...tagStyle,
            background: "rgba(155, 89, 182, 0.2)",
            color: "#9B59B6",
          }}
        >
          NOTE
        </span>
      )
    case NodeType.LOGIC:
      return (
        <span
          style={{
            ...tagStyle,
            background: "rgba(149, 165, 166, 0.2)",
            color: "#95A5A6",
          }}
        >
          LOGIC
        </span>
      )
  }
}

/**
 * Get formatted label for node with metadata badges
 */
export const getNodeLabel = (node: StructureNode): React.ReactNode => {
  const badges: React.ReactNode[] = []

  // Section metadata
  if (node.type === NodeType.SECTION) {
    const parts: string[] = []
    if (node.metadata?.questionCount) {
      parts.push(
        `${node.metadata.questionCount} question${
          node.metadata.questionCount !== 1 ? "s" : ""
        }`
      )
    }
    if (node.metadata?.textBlockCount) {
      parts.push(
        `${node.metadata.textBlockCount} text block${
          node.metadata.textBlockCount !== 1 ? "s" : ""
        }`
      )
    }
    if (parts.length > 0) {
      badges.push(
        <span key="section-meta" className="text-gray-500 text-xs ml-2">
          ({parts.join(", ")})
        </span>
      )
    }
  }

  // Question placeholder warning
  if (node.type === NodeType.QUESTION && node.metadata?.isPlaceholder) {
    badges.push(
      <span key="placeholder" className="text-orange-500 text-xs ml-1">
        ⚠️ PLACEHOLDER
      </span>
    )
  }

  // List metadata
  if (
    [NodeType.LIST, NodeType.RANGE_LIST, NodeType.EXCEPTIONS].includes(
      node.type
    )
  ) {
    // Item count
    badges.push(
      <span key="item-count" className="text-gray-500 text-xs ml-2">
        ({node.metadata?.itemCount || 0})
      </span>
    )

    // Reference count
    if (node.metadata?.referenceCount !== undefined) {
      if (node.metadata.referenceCount > 0) {
        badges.push(
          <span key="ref-count" className="text-green-600 text-xs ml-1">
            • {node.metadata.referenceCount} ref
            {node.metadata.referenceCount !== 1 ? "s" : ""}
          </span>
        )
      } else {
        badges.push(
          <span key="unused" className="text-yellow-600 text-xs ml-1">
            • UNUSED
          </span>
        )
      }
    }

    // Placeholder warning
    if (node.metadata?.isPlaceholder) {
      badges.push(
        <span key="placeholder" className="text-orange-500 text-xs ml-1">
          ⚠️ PLACEHOLDER
        </span>
      )
    }
  }

  // Exception item metadata (for child nodes under EXCEPTIONS)
  if (
    node.type === NodeType.EXCEPTION_ITEM &&
    node.metadata?.referenceCount !== undefined
  ) {
    if (node.metadata.referenceCount > 0) {
      badges.push(
        <span key="ref-count" className="text-green-600 text-xs ml-1">
          • {node.metadata.referenceCount}×
        </span>
      )
    } else if (node.metadata.isUnused) {
      badges.push(
        <span key="unused" className="text-yellow-600 text-xs ml-1">
          • unused
        </span>
      )
    }
  }

  return (
    <>
      {getNodeTag(node)}
      {node.name}
      {badges}
    </>
  )
}

/**
 * Recursively find a node by its key/id in the tree
 */
export const findNodeByKey = (
  tree: StructureNode,
  key: string
): StructureNode | null => {
  if (tree.id === key) return tree
  if (tree.children) {
    for (const child of tree.children) {
      const found = findNodeByKey(child, key)
      if (found) return found
    }
  }
  return null
}

/**
 * Calculate drop position based on mouse position within element bounds
 * @param mouseY - Mouse Y position
 * @param elementRect - Element bounding rectangle
 * @returns Position indicator: "before", "after", or "inside"
 */
export const calculateDropPosition = (
  mouseY: number,
  elementRect: DOMRect
): "before" | "after" | "inside" => {
  const mouseYRelative = mouseY - elementRect.top
  const height = elementRect.height

  // More generous zones: 40% top = before, 40% bottom = after, 20% middle = inside
  if (mouseYRelative < height * 0.4) {
    return "before"
  } else if (mouseYRelative > height * 0.6) {
    return "after"
  } else {
    return "inside"
  }
}

/**
 * Find the closest tree node to a given Y position
 * @param mouseY - Mouse Y coordinate
 * @returns Object containing the closest node element, distance, and nodeKey
 */
export const findClosestNode = (
  mouseY: number
): { element: HTMLElement; distance: number; nodeKey: string } | null => {
  const treeNodes = document.querySelectorAll(".ant-tree-treenode")
  let closestNode = null as {
    element: HTMLElement
    distance: number
    nodeKey: string
  } | null

  treeNodes.forEach((treeNode) => {
    const contentWrapper = treeNode.querySelector(
      ".ant-tree-node-content-wrapper"
    )
    if (!contentWrapper) return

    const rect = contentWrapper.getBoundingClientRect()
    const nodeCenter = rect.top + rect.height / 2
    const distance = Math.abs(mouseY - nodeCenter)

    if (!closestNode || distance < closestNode.distance) {
      const wrapper = contentWrapper.querySelector(
        ".structure-node-wrapper"
      ) as HTMLElement
      const nodeKey = wrapper?.getAttribute("data-node-key")
      if (nodeKey) {
        closestNode = {
          element: contentWrapper as HTMLElement,
          distance,
          nodeKey,
        }
      }
    }
  })

  return closestNode
}
